import { themeVariables } from '@noths/polaris-design-system-abstract-theme';
import { css } from 'styled-components';

/**
 * The Most Loved pill's 4px corner and hairline shadow have no theme token
 * yet, so both are literals here. The nearest radius token is
 * `borderRadius.md` at 6px, which reads as a noticeably softer pill than the
 * design, and there is no elevation scale at all.
 */
const PILL_BORDER_RADIUS = '4px';
const PILL_SHADOW = '0 0 2px rgb(14 14 17 / 10%)';

/**
 * The width at which the heading, rating and Most Loved pill stop stacking and
 * sit on one row, as the wider design shows. Measured against the component's
 * own inline size rather than the viewport, so the section lays itself out
 * from the space it is given wherever it is placed. Roughly the sum of the
 * three items at their natural widths plus the gaps between them.
 */
const HEADER_ROW_MIN_WIDTH = '640px';

export const root = css`
  container-type: inline-size;
  display: flex;
  flex-direction: column;
  row-gap: var(${themeVariables.spacing.md});
  font-family: var(${themeVariables.typography.body.fontFamily});
  font-weight: var(${themeVariables.typography.body.fontWeight});
  color: var(${themeVariables.colors.content.default});
`;

export const header = css`
  display: flex;
  flex-direction: column;
  row-gap: var(${themeVariables.spacing.lg});

  @container (min-width: ${HEADER_ROW_MIN_WIDTH}) {
    flex-direction: row;
    align-items: center;
    column-gap: var(${themeVariables.spacing.md});
  }
`;

export const heading = css`
  margin: 0;
  font-size: var(${themeVariables.typography.heading.sizes.sm.fontSize});
  line-height: var(${themeVariables.typography.heading.sizes.sm.lineHeight});
`;

export const ratingContainer = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: var(${themeVariables.spacing.md});

  @container (min-width: ${HEADER_ROW_MIN_WIDTH}) {
    flex-direction: row;
    align-items: center;
    column-gap: var(${themeVariables.spacing.md});
  }
`;

export const rating = css`
  display: flex;
  margin: 0;
  align-items: center;
  column-gap: var(${themeVariables.spacing.xxs});
  color: var(${themeVariables.colors.content.brand});
  font-family: var(${themeVariables.typography.heading.fontFamily});
  font-weight: var(${themeVariables.typography.heading.fontWeight});
  font-size: var(${themeVariables.typography.heading.sizes.md.fontSize});
  line-height: var(${themeVariables.typography.heading.sizes.md.lineHeight});
  letter-spacing: var(${themeVariables.typography.heading.letterSpacing});

  svg {
    flex-shrink: 0;
  }
`;

/*
 * Ordinary text flow rather than a flex row. The pill is one sentence with an
 * icon in the middle of it, and as flex items each fragment shrank and wrapped
 * on its own, so on a narrow screen the sentence broke into columns with gaps
 * through it instead of wrapping as prose. The icon goes inline the way
 * ProductBadge sets its own.
 */
export const mostLoved = css`
  margin: 0;
  padding: var(${themeVariables.spacing.xs}) var(${themeVariables.spacing.sm});
  border: var(${themeVariables.strokeWidth}) solid var(${themeVariables.colors.stroke.default});
  border-radius: ${PILL_BORDER_RADIUS};
  background-color: var(${themeVariables.colors.surface.default});
  box-shadow: ${PILL_SHADOW};
  font-size: var(${themeVariables.typography.body.sizes.sm.fontSize});
  line-height: var(${themeVariables.typography.body.sizes.sm.lineHeight});

  svg {
    display: inline-block;
    margin-right: var(${themeVariables.spacing.xxs});
    vertical-align: text-bottom;
    color: var(${themeVariables.colors.content.brand});
  }
`;

export const mostLovedLabel = css`
  color: var(${themeVariables.colors.content.brand});
  font-weight: bold;
  /* Keeps the badge name intact rather than breaking between its two words. */
  white-space: nowrap;
`;

export const details = css`
  display: flex;
  flex-direction: column;
  row-gap: var(${themeVariables.spacing.sm});
  font-size: var(${themeVariables.typography.body.sizes.md.fontSize});
  line-height: var(${themeVariables.typography.body.sizes.md.lineHeight});
`;

export const summaryText = css`
  margin: 0;
  font-size: inherit;
  line-height: inherit;
  overflow-wrap: break-word;
`;

export const summaryNote = css`
  margin: 0;
  color: var(${themeVariables.colors.content.secondary});
  font-size: inherit;
  line-height: inherit;
`;

export const divider = css`
  height: var(${themeVariables.strokeWidth});
  border: 0;
  margin: 0;
  background-color: var(${themeVariables.colors.stroke.default});
`;

export const asAdvertised = css`
  display: flex;
  margin: 0;
  /*
   * Top rather than centre so the tick stays level with the first line when the
   * sentence wraps on a narrow screen, instead of drifting to the middle of the
   * block, while the wrapped text keeps its own column beside the tick.
   */
  align-items: flex-start;
  column-gap: var(${themeVariables.spacing.xxs});
  font-size: var(${themeVariables.typography.body.sizes.md.fontSize});
  line-height: var(${themeVariables.typography.body.sizes.md.lineHeight});

  svg {
    flex-shrink: 0;
    color: var(${themeVariables.colors.content.positive});
  }
`;
